<div *ngIf="!isPageReady" class="flex h-full items-center justify-center">
  <p-progressSpinner ariaLabel="loading"></p-progressSpinner>
</div>

<div *ngIf="isPageReady" class="flex h-full flex-col">
  <!-- Top section: All cards on one row -->
  <div class="grid shrink-0 grid-cols-12 gap-4 p-4">
    <!-- Info Card -->
    <div class="qb-detail-card qb-primary-tint col-span-3 p-4">
      <div class="mb-3 border-b pb-2 text-base font-semibold uppercase tracking-wide">Info</div>
      <table class="qb-info-table flush">
        <tr>
          <td class="label">Invoice #</td>
          <td class="value">{{ invoiceHeader.id }}</td>
        </tr>
        <tr>
          <td class="label">Invoice Date</td>
          <td class="value">{{ invoiceHeader.invoiceDate | date: 'MM/dd/yyyy' }}</td>
        </tr>
        <tr>
          <td class="label">Account</td>
          <td class="value">{{ invoiceHeader.joHeader?.account?.accountName }}</td>
        </tr>
        <tr>
          <td class="label">Employee</td>
          <td class="value">{{ employeeName }}</td>
        </tr>
        <tr>
          <td class="label">Status</td>
          <td class="value">{{ dbLookup('InvoiceStatus', invoiceHeader.invoiceStatusId) }}</td>
        </tr>
        <tr>
          <td class="label">Due Date</td>
          <td class="value">{{ invoiceHeader.dueDate | date: 'MM/dd/yyyy' }}</td>
        </tr>
      </table>
    </div>

    <!-- Addresses Card -->
    <div class="qb-detail-card qb-primary-tint col-span-4 p-4">
      <div class="mb-3 border-b pb-2 text-base font-semibold uppercase tracking-wide">Addresses</div>
      <div class="flex gap-8">
        <!-- Bill To -->
        <div class="qb-stacked-field flex-1">
          <div class="label mb-1 text-sm font-semibold">Bill To</div>
          <div class="value leading-relaxed">
            <div>{{ invoiceHeader.joHeader?.billingName }}</div>
            <div>{{ invoiceHeader.joHeader?.billingAddress1 }}</div>
            <div *ngIf="invoiceHeader.joHeader?.billingAddress2">{{ invoiceHeader.joHeader?.billingAddress2 }}</div>
            <div *ngIf="invoiceHeader.joHeader?.billingAddress3">{{ invoiceHeader.joHeader?.billingAddress3 }}</div>
            <div>
              {{ invoiceHeader.joHeader?.billingCity }}, {{ invoiceHeader.joHeader?.billingState }}
              {{ invoiceHeader.joHeader?.billingZipCode }}
            </div>
            <div *ngIf="invoiceHeader.joHeader?.billingTelephone" class="mt-2">{{ invoiceHeader.joHeader?.billingTelephone }}</div>
          </div>
        </div>

        <!-- Ship To (only for non-account-owned orders) -->
        <div *ngIf="!invoiceHeader.joHeader?.isAccountOwnedOrder" class="qb-stacked-field flex-1">
          <div class="label mb-1 text-sm font-semibold">Ship To</div>
          <div class="value leading-relaxed">
            <div>{{ invoiceHeader.joHeader?.shippingName }}</div>
            <div>{{ invoiceHeader.joHeader?.shippingAddress1 }}</div>
            <div *ngIf="invoiceHeader.joHeader?.shippingAddress2">{{ invoiceHeader.joHeader?.shippingAddress2 }}</div>
            <div *ngIf="invoiceHeader.joHeader?.shippingAddress3">{{ invoiceHeader.joHeader?.shippingAddress3 }}</div>
            <div>
              {{ invoiceHeader.joHeader?.shippingCity }}, {{ invoiceHeader.joHeader?.shippingState }}
              {{ invoiceHeader.joHeader?.shippingZipCode }}
            </div>
            <div *ngIf="invoiceHeader.joHeader?.shippingTelephone" class="mt-2">{{ invoiceHeader.joHeader?.shippingTelephone }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Shipping Card -->
    <div class="qb-detail-card qb-primary-tint col-span-3 p-4">
      <div class="mb-3 border-b pb-2 text-base font-semibold uppercase tracking-wide">Shipment and Invoice</div>
      <ng-container *ngIf="invoiceHeader.invoiceStatusId == 'POSTED'; else notShipped">
        <table class="qb-info-table flush">
          <tr>
            <td class="label">Ship Date</td>
            <td class="value">{{ invoiceHeader.shipTs | date: 'MM/dd/yyyy' }}</td>
          </tr>
          <tr>
            <td class="label">Delivered By</td>
            <td class="value">{{ deliveredBy }}</td>
          </tr>
          <tr>
            <td class="label">Saved to PDF Dir.</td>
            <td class="value">{{ savedToPdf }}</td>
          </tr>
          <tr>
            <td class="label">Manifest</td>
            <td class="value">{{ manifestName }}</td>
          </tr>
          <tr>
            <td class="label">Carrier</td>
            <td class="value">{{ carrierName }}</td>
          </tr>
          <tr *ngIf="trackingNumber">
            <td class="label">Tracking #</td>
            <td class="value">{{ trackingNumber }}</td>
          </tr>
          <tr>
            <td class="label">Freight Cost</td>
            <td class="value">{{ invoiceHeader.calcShipFreightAmt() | currency }}</td>
          </tr>
        </table>
        <div class="mt-2 text-xs text-muted-color">
          {{ invoiceHeader.shipFreightInfo }}
        </div>
      </ng-container>
      <ng-template #notShipped>
        <!-- <div class="text-muted italic">This voucher was originally scheduled to ship by: {{ invoiceHeader.joHeader?.getJoShipDate() }}</div> -->
        <div class="text-muted italic">This voucher is queued for shipment.</div>
      </ng-template>
    </div>

    <!-- Summary Card -->
    <div class="qb-detail-card qb-primary-tint col-span-2 p-4">
      <div class="mb-3 border-b pb-2 text-base font-semibold uppercase tracking-wide">Summary</div>
      <table class="qb-info-table flush w-full">
        <tr>
          <td class="label">Subtotal New</td>
          <td class="value text-right">{{ invoiceHeader.calcExtendedAmt() || 0 | currency }}</td>
        </tr>
        <tr>
          <td class="label">Subtotal Reissue</td>
          <td class="value text-right">{{ invoiceHeader.calcExtendedReissueAmt() || 0 | currency }}</td>
        </tr>
        <tr>
          <td class="label">Subtotal</td>
          <td class="value text-right">{{ subtotalAmt | currency }}</td>
        </tr>
        <tr>
          <td class="label">Tax ({{ invoiceHeader.joHeader?.taxRate | percent: '1.2-2' }})</td>
          <td class="value text-right">{{ invoiceHeader.taxAmt || 0 | currency }}</td>
        </tr>
        <tr>
          <td class="label">Freight</td>
          <td class="value text-right">{{ invoiceHeader.calcShipFreightAmt() | currency }}</td>
        </tr>
        <tr>
          <td class="label">Handling</td>
          <td class="value text-right">{{ invoiceHeader.orderHandlingAmt || 0 | currency }}</td>
        </tr>
        <tr>
          <td class="label">Prepaid</td>
          <td class="value text-right">({{ invoiceHeader.prepaymentAmt || 0 | currency }})</td>
        </tr>
        <tr *ngIf="invoiceHeader.pCardAmt">
          <td class="label">P-Card</td>
          <td class="value text-right">({{ invoiceHeader.pCardAmt || 0 | currency }})</td>
        </tr>
        <tr class="font-semibold">
          <td class="label">Total</td>
          <td class="value text-right">{{ invoiceHeader.calcTotalAmt() | currency }}</td>
        </tr>
      </table>
    </div>
  </div>

  <!-- Invoice Details Grid -->
  <div class="min-h-0 flex-1 px-4">
    <app-ag-grid [gridOptions]="invdGridOptions" [rowData]="legRows" class="h-full"></app-ag-grid>
  </div>

  <!-- Footer with Close button -->
  <div class="flex shrink-0 justify-start border-t border-gray-200 p-3">
    <button pButton type="button" class="p-button-raised" (click)="onClose()">
      <span>Close</span>
    </button>
  </div>
</div>
